// M004-F0745 section 6.2 - the move toast. "Moved 2 receipts to <name>.  Undo" for 6 seconds, PAUSED while the pointer is over it or focus
// is inside it (the clock keeps what is left, never restarts), role="status". A refusal turns it into role="alert" carrying the Ledger's
// sentence and its code (a little longer, so the code can be read). The live announcement itself is the workspace announcer
// ("Press Z to undo"); this element is therefore aria-live="off" so a screen reader is not told twice.
import React, { useEffect, useRef, useState } from 'react';
import { UNDO_WINDOW_MS } from './envelopeMoveModel';

const ERROR_WINDOW_MS = 9000;

export default function MoveToast({ toast, onUndo, onDismiss }) {
  const [held, setHeld] = useState({ hover: false, focus: false });
  const paused = held.hover || held.focus;
  const remaining = useRef(0);
  const started = useRef(0);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  const toastId = toast?.id;
  const duration = toast?.kind === 'error' ? ERROR_WINDOW_MS : UNDO_WINDOW_MS;

  useEffect(() => { remaining.current = duration; setHeld({ hover: false, focus: false }); }, [toastId, duration]);

  useEffect(() => {
    if (!toastId || paused || toast?.kind === 'pending') return undefined; // a pending toast stays until the final answer replaces it
    started.current = Date.now();
    const timer = setTimeout(() => dismissRef.current(toastId), Math.max(0, remaining.current));
    return () => { clearTimeout(timer); remaining.current -= Date.now() - started.current; };
  }, [toastId, paused, toast?.kind]);

  if (!toast) return null;
  const isError = toast.kind === 'error';
  return (
    <div
      className={`ew-move-toast ew-move-toast-${toast.kind}`}
      role={isError ? 'alert' : 'status'}
      aria-live="off"
      aria-atomic="true"
      data-testid="envelope-move-toast"
      data-paused={paused ? 'true' : undefined}
      onMouseEnter={() => setHeld((current) => ({ ...current, hover: true }))}
      onMouseLeave={() => setHeld((current) => ({ ...current, hover: false }))}
      onFocus={() => setHeld((current) => ({ ...current, focus: true }))}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setHeld((current) => ({ ...current, focus: false })); }}
    >
      <span className="ew-move-toast-text" data-testid="envelope-move-toast-text">
        {toast.text}
        {toast.code ? <> <code data-testid="envelope-move-toast-code">{toast.code}</code></> : null}
      </span>
      {toast.undo ? (
        <button type="button" className="ew-move-undo" data-testid="envelope-move-undo" aria-keyshortcuts="Z Control+Z Meta+Z" onClick={onUndo}>Undo</button>
      ) : null}
      <button type="button" className="ew-move-dismiss" data-testid="envelope-move-dismiss" aria-label="Dismiss" onClick={() => onDismiss(toast.id)}>
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M3.3 2.3 8 7l4.7-4.7 1 1L9 8l4.7 4.7-1 1L8 9l-4.7 4.7-1-1L7 8 2.3 3.3l1-1Z" /></svg>
      </button>
    </div>
  );
}
